import { useEffect, useMemo, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { useNotify, useTranslate } from 'react-admin';

import { formatInt, formatMoneyShort, formatUSD, downloadBlob } from 'src/utils/format';
import type { DashboardKpi } from '../components/KpiCard';
import type { PlatformDonutItem, PlatformTotals } from '../components/PlatformDonutCard';
import type { PerformancePoint } from '../components/PerformanceCard';
import type { CoinDistItem } from '../components/CoinDistributionCard';
import type { UserVolumeDistItem } from '../components/UserVolumeDistributionCard';

import {
  getPerpsKpis,
  getPerpsPerformance,
  getPerpsCoinDistribution,
  getPerpsUserVolumeDistribution,
  exportPerpsDashboardCsv,
} from '../services/perpetualsDashboard.service';
import type {
  PerpsKpisResp,
  PerpsPerformanceResp,
  PerpsCoinDistributionResp,
  PerpsUserVolumeDistributionResp,
} from '../services/perpetualsDashboard.types';
import { getGa4TrafficPath } from '../services/ga4TrafficPath.service';
import type { Ga4TrafficPathResp } from '../services/ga4TrafficPath.types';

export type QuickRange = '24h' | '7d' | '30d' | 'all' | 'custom';
export type DashboardV2TabKey = 'perpetuals' | 'meme' | 'user';

export type DashboardV2Data = {
  kpisTop: DashboardKpi[];
  kpisMid: DashboardKpi[];
  platforms: PlatformDonutItem[];
  platformTotals: PlatformTotals;
  performance: PerformancePoint[];
  coinDist: CoinDistItem[];
  userVolumeDist: UserVolumeDistItem[];
  totalVolume: Number;
  exporting: boolean;
  exportCSV: () => Promise<void>;
};

function clampNumber(n: number) {
  return Number.isFinite(n) ? n : 0;
}

function pctChange(current: number, prev: number) {
  const p = clampNumber(prev);
  if (p === 0) return null;
  return ((current - p) / p) * 100;
}

function signedPct(n: number | null) {
  if (n === null) return '--';
  const v = Math.round(n * 10) / 10;
  return `${v >= 0 ? '+' : ''}${v}%`;
}

function signedInt(n: number) {
  const v = Math.round(n);
  return `${v >= 0 ? '+' : ''}${v}`;
}

function getPeriodLabel(t: ReturnType<typeof useTranslate>, range: QuickRange) {
  if (range === '30d') return t('module.dashboardV2.period.lastMonth', { _: 'last month' });
  if (range === '24h') return t('module.dashboardV2.period.prev24h', { _: 'previous 24h' });
  if (range === '7d') return t('module.dashboardV2.period.prev7d', { _: 'previous 7 days' });
  if (range === 'all') return t('module.dashboardV2.period.prevPeriod', { _: 'previous period' });
  return t('module.dashboardV2.period.prevPeriod', { _: 'previous period' });
}

function pickGranularity(fromMs: number, toMs: number): 'hour' | 'day' {
  const dur = toMs - fromMs;
  const oneDay = 24 * 60 * 60 * 1000;
  return dur <= oneDay ? 'hour' : 'day';
}


function mapPlatform(
  resp?: Ga4TrafficPathResp
): { platforms: PlatformDonutItem[]; platformTotals: PlatformTotals } {
  const items = resp?.items ?? [];

  const totalUsers =
    Number(resp?.total?.users ?? 0) ||
    items.reduce((s, it) => s + Number(it.users ?? 0), 0);

  // 顶部数字：用 users
  const platformTotals: PlatformTotals = Object.fromEntries(
    items.map((it) => [it.key, Math.round(Number(it.users ?? 0))])
  );

  // donut 百分比：用 users 占比
  const platforms: PlatformDonutItem[] = items.map((it) => {
    const users = Number(it.users ?? 0);
    const pct = totalUsers > 0 ? (users / totalUsers) * 100 : 0;

    return {
      name: it.key,
      value: Math.round(pct * 10) / 10,
      users,
      views: Number(it.views ?? 0),
      viewsPerUser: Number(it.viewsPerUser ?? 0),
    };
  });

  const ORDER = ['Desktop', 'App (Android)', 'App (iOS)', 'Mobile Web'] as const;
  const orderIndex: Map<string, number> = new Map(ORDER.map((k, i) => [k, i]));
  platforms.sort((a, b) => (orderIndex.get(a.name) ?? 999) - (orderIndex.get(b.name) ?? 999));

  return { platforms, platformTotals };
}

function mapKpisToCards(
  t: ReturnType<typeof useTranslate>,
  range: QuickRange,
  kpis: PerpsKpisResp
) {
  const periodLabel = getPeriodLabel(t, range);

  const topMap = Object.fromEntries(kpis.kpisTop.map((x) => [x.key, x]));
  const midMap = Object.fromEntries(kpis.kpisMid.map((x) => [x.key, x]));

  const revenuePct = pctChange(topMap.revenue?.value ?? 0, topMap.revenue?.prevValue ?? 0);
  const volumePct = pctChange(topMap.volume?.value ?? 0, topMap.volume?.prevValue ?? 0);
  const usersPct = pctChange(topMap.users?.value ?? 0, topMap.users?.prevValue ?? 0);
  const newUsersPct = pctChange(topMap.newUsers?.value ?? 0, topMap.newUsers?.prevValue ?? 0);

  const returningPct = pctChange(
    midMap.returningUserRevenue?.value ?? 0,
    midMap.returningUserRevenue?.prevValue ?? 0
  );
  const newUserRevPct = pctChange(
    midMap.newUserRevenue?.value ?? 0,
    midMap.newUserRevenue?.prevValue ?? 0
  );

  const kpisTop: DashboardKpi[] = [
    {
      label: t('module.dashboardV2.kpi.revenue', { _: 'Revenue' }),
      value: formatUSD(topMap.revenue?.value ?? 0),
      deltaText: `${signedPct(revenuePct)} from ${periodLabel}`,
      // deltaText: t('dashboardV2.delta.pctFromPeriod', {
      //   _: '%{pct} from %{period}',
      //   pct: signedPct(revenuePct),
      //   period: periodLabel,
      // }),
    },
    {
      label: t('module.dashboardV2.kpi.volume', { _: 'Volume' }),
      value: formatMoneyShort(topMap.volume?.value ?? 0),
      deltaText: `${signedPct(volumePct)} from ${periodLabel}`,
      // deltaText: t('dashboardV2.delta.pctFromPeriod', {
      //   _: '%{pct} from %{period}',
      //   pct: signedPct(volumePct),
      //   period: periodLabel,
      // }),
    },
    {
      label: t('module.dashboardV2.kpi.users', { _: 'Users' }),
      value: formatInt(topMap.users?.value ?? 0),
      deltaText: `${signedPct(usersPct)} from ${periodLabel}`,
      // deltaText: t('dashboardV2.delta.pctFromPeriod', {
      //   _: '%{pct} from %{period}',
      //   pct: signedPct(usersPct),
      //   period: periodLabel,
      // }),
    },
    {
      label: t('module.dashboardV2.kpi.newUsers', { _: 'New Users' }),
      value: formatInt(topMap.newUsers?.value ?? 0),
      deltaText: `${signedPct(newUsersPct)} from ${periodLabel}`,
      // deltaText: t('dashboardV2.delta.intFromPeriod', {
      //   _: '%{pct} from %{period}',
      //   pct: signedPct(newUsersPct),
      //   period: periodLabel,
      // }),
    },
  ];

  const kpisMid: DashboardKpi[] = [
    {
      label: t('module.dashboardV2.kpi.returningUserRevenue', { _: 'Returning User Revenue' }),
      value: formatUSD(midMap.returningUserRevenue?.value ?? 0),
      deltaText: `${signedPct(returningPct)} from ${periodLabel}`,
      // deltaText: t('dashboardV2.delta.pctFromPeriod', {
      //   _: '%{pct} from %{period}',
      //   pct: signedPct(returningPct),
      //   period: periodLabel,
      // }),
    },
    {
      label: t('module.dashboardV2.kpi.newUserRevenue', { _: 'New User Revenue' }),
      value: formatUSD(midMap.newUserRevenue?.value ?? 0),
      deltaText: `${signedPct(newUserRevPct)} from ${periodLabel}`,
      // deltaText: t('dashboardV2.delta.pctFromPeriod', {
      //   _: '%{pct} from %{period}',
      //   pct: signedPct(newUserRevPct),
      //   period: periodLabel,
      // }),
    },
  ];

  return { kpisTop, kpisMid };
}

function mapPerformanceToPoints(perf: PerpsPerformanceResp): PerformancePoint[] {
  return perf.series.map((p) => ({
    date: p.label,
    users: p.users,
    volume: p.volume,
    revenue: p.revenue,
  }));
}

function mapCoinDist(coin: PerpsCoinDistributionResp): CoinDistItem[] {
  return coin.items.map((it) => ({
    coin: it.coin || null,
    volume: it.volume,
    users: it.users,
    ...(it.symbol ? { symbol: it.symbol } : {}),
  })) as any;
}

function mapUserVolumeDist(u: PerpsUserVolumeDistributionResp): UserVolumeDistItem[] {
  return u.items.map((it) => ({
    bucket: it.bucket,
    percent: it.percent,
    volume: it.volume,
    users: it.users,
  }));
}

export function useDashboardV2Data(params: {
  tab: DashboardV2TabKey;
  range: QuickRange;
  from: Dayjs;
  to: Dayjs;
  timezone: string;
}) {
  const { tab, range, from, to, timezone } = params;
  const t = useTranslate();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [raw, setRaw] = useState<{
    kpis?: PerpsKpisResp;
    perf?: PerpsPerformanceResp;
    coin?: PerpsCoinDistributionResp;
    userVol?: PerpsUserVolumeDistributionResp;
    platform?: Ga4TrafficPathResp;
  } | null>(null);

  useEffect(() => {
    if (tab !== 'perpetuals') {
      setRaw(null);
      return;
    }

    const fromMs = from.valueOf();
    const toMs = to.valueOf();
    const granularity = pickGranularity(fromMs, toMs);

    let alive = true;
    setLoading(true);

    Promise.all([
      getPerpsKpis({ from: fromMs, to: toMs, timezone }),
      getPerpsPerformance({ from: fromMs, to: toMs, timezone, granularity }),
      getPerpsCoinDistribution({ from: fromMs, to: toMs, timezone, limit: 7 }),
      getPerpsUserVolumeDistribution({ from: fromMs, to: toMs, timezone }),
      getGa4TrafficPath({ from: fromMs, to: toMs, timezone, path: '/futures/BTC', pathMatch: 'PREFIX' }),
    ])
      .then(([kpis, perf, coin, userVol, platform]) => {
        if (!alive) return;
        setRaw({ kpis, perf, coin, userVol, platform });
      })
      .catch((e: any) => {
        if (!alive) return;
        notify(e?.message ?? t('module.dashboardV2.fetchFailed', { _: 'Fetch failed' }), { type: 'warning' });
        setRaw(null);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [tab, from, to, timezone, t, notify]);

  const data = useMemo<DashboardV2Data | null>(() => {
    if (tab !== 'perpetuals') return null;
    if (!raw?.kpis || !raw?.perf || !raw?.coin || !raw?.userVol) return null;

    const { platforms, platformTotals } = mapPlatform(raw.platform);

    const { kpisTop, kpisMid } = mapKpisToCards(t, range, raw.kpis);
    const performance = mapPerformanceToPoints(raw.perf);
    const coinDist = mapCoinDist(raw.coin);
    const userVolumeDist = mapUserVolumeDist(raw.userVol);
    const totalVolume = raw.userVol.totalVolume;


    // const exportCSV = () => {
    //   try {
    //     downloadCSV(`dashboard-v2-${tab}-${range}.csv`, [
    //       { section: 'kpi', ...Object.fromEntries(kpisTop.map((k) => [k.label, k.value])) },
    //       { section: 'kpi_mid', ...Object.fromEntries(kpisMid.map((k) => [k.label, k.value])) },
    //       ...performance.map((p) => ({ section: 'performance', ...p })),
    //       ...coinDist.map((c: any) => ({ section: 'coinDistribution', ...c })),
    //       ...userVolumeDist.map((u) => ({ section: 'userVolumeDistribution', ...u })),
    //     ]);
    //     notify(t('dashboardV2.exportSuccess', { _: 'Export success' }), { type: 'info' });
    //   } catch (e: any) {
    //     notify(e?.message ?? t('dashboardV2.exportFailed', { _: 'Export failed' }), { type: 'warning' });
    //   }
    // };

    const DAY_MS = 24 * 60 * 60 * 1000;
    const MAX_EXPORT_DAYS = 366;

    const exportCSV = async () => {
      if (exporting) return;
      setExporting(true);

      try {
        const fromMs = from.valueOf();

        const toMsExclusive = to.endOf('day').valueOf() + 1;

        const days = Math.ceil((toMsExclusive - fromMs) / DAY_MS);

        if (days > MAX_EXPORT_DAYS) {
          notify(t('module.dashboardV2.exportMax366Days', { _: '最多只能导出366天数据！' }), { type: 'warning' });
          return;
        }

        const { blob, filename } = await exportPerpsDashboardCsv({
          from: fromMs,
          to: toMsExclusive,
          timezone,
          maxDays: MAX_EXPORT_DAYS,
        });

        downloadBlob(filename, blob);
        notify(t('module.dashboardV2.exportSuccess', { _: 'Export success' }), { type: 'info' });
      } catch (e: any) {
        notify(e?.message ?? t('module.dashboardV2.exportFailed', { _: 'Export failed' }), { type: 'warning' });
      } finally {
        setExporting(false);
      }
    };

    return {
      kpisTop,
      kpisMid,
      platforms,
      platformTotals,
      performance,
      coinDist,
      userVolumeDist,
      totalVolume,
      exporting,
      exportCSV,
    };
  }, [raw, range, tab, t, notify, from, to, timezone, exporting]);

  return { data, loading };
}
